import { useCallback } from 'react'

import { useNavigate } from 'react-router-dom'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import {
  useAbandonCheckoutMutation,
  useGetOrdersQuery,
  useLazyGetCurrentOrderQuery,
} from 'src/Redux/artistOrderApi'
import {
  CampaignPayment,
  getCampaignPayment,
} from 'src/Redux/artistOrderApi/campaignPayment'
import { getCampaignCheckoutRoute } from 'src/Redux/artistOrderApi/campaignPaymentRoutes'
import { hasRecentPaymentReturn } from 'src/Utils/paymentReturnMarks'

type GetCampaignPayment = (
  platform: CampaignPlatformTypeEnum,
  campaignId: number,
) => CampaignPayment | null

export const useCampaignPayments = (): GetCampaignPayment => {
  const { data: orders } = useGetOrdersQuery(void 0)

  return useCallback(
    (platform, campaignId) =>
      getCampaignPayment(
        orders ?? [],
        platform,
        campaignId,
        hasRecentPaymentReturn(platform, campaignId),
      ),
    [orders],
  )
}

export const useCampaignPayment = (
  platform: CampaignPlatformTypeEnum,
  campaignId?: number,
): CampaignPayment | null => {
  const getPayment = useCampaignPayments()
  return campaignId ? getPayment(platform, campaignId) : null
}

interface UseRetryCampaignPayment {
  retryPayment: (
    platform: CampaignPlatformTypeEnum,
    campaignId: number,
    orderId: number,
  ) => Promise<boolean>
  isRetrying: boolean
}

export const useRetryCampaignPayment = (): UseRetryCampaignPayment => {
  const navigate = useNavigate()
  const [abandonCheckout, { isLoading: isAbandoning }] =
    useAbandonCheckoutMutation()
  const [getCurrentOrder, { isFetching: isRefetchingOrder }] =
    useLazyGetCurrentOrderQuery()

  const retryPayment = useCallback(
    async (
      platform: CampaignPlatformTypeEnum,
      campaignId: number,
      orderId: number,
    ): Promise<boolean> => {
      try {
        await abandonCheckout({ orderId }).unwrap()
        await getCurrentOrder().unwrap()
      } catch {
        return false
      }

      navigate(getCampaignCheckoutRoute(platform, campaignId))
      return true
    },
    [abandonCheckout, getCurrentOrder, navigate],
  )

  return { retryPayment, isRetrying: isAbandoning || isRefetchingOrder }
}
